import React, { useRef, useState } from 'react';
import { emptyMessage } from "./ui";
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ImageCropperDialog } from './ImageCropperDialog';
import { uploadImageToS3, checkExactDimensions } from '../utils/functions/uploadToS3';
import toast from 'react-hot-toast';

interface ImageUrlsManagerProps {
  imageUrls: string[];
  onChange: (urls: string[]) => void;
  label?: string;
  /** Width in px for the crop area. Defaults to 1290 */
  cropWidth?: number;
  /** Height in px for the crop area. Defaults to 900 */
  cropHeight?: number;
  disabled?: boolean;
}

export const ImageUrlsManager: React.FC<ImageUrlsManagerProps> = ({
  imageUrls,
  onChange,
  label = "Imágenes Adicionales",
  cropWidth = 1290,
  cropHeight = 900,
  disabled = false,
}) => {
  const [cropperImage, setCropperImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.currentTarget;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Check if image exactly matches target dimensions → skip cropper
    const { exact } = await checkExactDimensions(file, cropWidth, cropHeight);

    if (exact) {
      // Exact match: upload directly
      setUploading(true);
      try {
        const blob = new Blob([file], { type: file.type || "image/jpeg" });
        const s3Url = await uploadImageToS3(blob, "gallery", file.name);
        onChange([...imageUrls, s3Url]);
        toast.success("Imagen subida exitosamente");
      } catch (err) {
        console.error("Upload error:", err);
        toast.error("Error al subir la imagen");
      } finally {
        setUploading(false);
      }
    } else {
      // Open cropper
      const reader = new FileReader();
      reader.onload = () => {
        setCropperImage(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCropConfirm = async (croppedBlob: Blob) => {
    setShowCropper(false);
    setCropperImage(null);
    setUploading(true);

    try {
      const s3Url = await uploadImageToS3(croppedBlob, "gallery", "gallery-image.jpg");
      onChange([...imageUrls, s3Url]);
      toast.success("Imagen subida exitosamente");
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setCropperImage(null);
  };

  const handleRemoveUrl = (indexToRemove: number) => {
    const updatedUrls = imageUrls.filter((_, index) => index !== indexToRemove);
    onChange(updatedUrls);
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= imageUrls.length) return;
    const updated = [...imageUrls];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    onChange(updated);
  };

  return (
    <div className="flex flex-column gap-2">
      <label className="font-semibold">{label}</label>
      <small className="text-500">
        Podés agregar varias imágenes. La primera se mostrará como portada de la galería. Resolución mínima: {cropWidth}x{cropHeight}px.
      </small>

      <div
        className="border-1 surface-border border-round p-3 flex flex-column gap-3"
        style={{ background: "#f8f9fa" }}
      >
        {/* Upload button */}
        <div className="flex gap-2 align-items-center flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            id="galleryImageInput"
            onChange={handleFileSelect}
            disabled={disabled || uploading}
          />
          <label
            htmlFor="galleryImageInput"
            className={`flex align-items-center cursor-pointer border-1 border-round text-primary px-4 py-2 ${
              disabled || uploading ? "disabled-label" : ""
            }`}
            style={{
              opacity: disabled || uploading ? 0.6 : 1,
              background: "white",
            }}
          >
            <i className="pi pi-plus mr-2"></i>
            Agregar imagen
            <i className="pi pi-upload ml-2"></i>
          </label>
          {uploading && (
            <div className="flex align-items-center gap-2">
              <ProgressSpinner style={{ width: "20px", height: "20px" }} />
              <span className="text-sm text-500">Subiendo...</span>
            </div>
          )}
        </div>

        {/* Display current images */}
        {imageUrls.length > 0 ? (
          <>
            <Divider className="my-0" />
            <div className="flex flex-column gap-2">
              <span className="font-medium text-sm text-600">
                Imágenes agregadas ({imageUrls.length})
              </span>

              <div className="grid">
                {imageUrls.map((url, index) => (
                  <div key={`${url}-${index}`} className="col-6 md:col-4 lg:col-3">
                    <div
                      className="border-1 surface-border border-round overflow-hidden flex flex-column"
                      style={{ background: "white", height: "100%" }}
                    >
                      {/* Image preview area */}
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          aspectRatio: `${cropWidth} / ${cropHeight}`,
                          background: "#e9ecef",
                        }}
                      >
                        <img
                          src={url}
                          alt={`Imagen ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        {/* Position badge */}
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            background: "rgba(0,0,0,0.7)",
                            color: "white",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </div>

                        {/* Remove button (floating) */}
                        <Button
                          type="button"
                          icon="pi pi-times"
                          rounded
                          severity="danger"
                          size="small"
                          onClick={() => handleRemoveUrl(index)}
                          disabled={disabled || uploading}
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 30,
                            height: 30,
                          }}
                          aria-label="Eliminar imagen"
                        />
                      </div>

                      {/* Reorder controls */}
                      <div className="flex justify-content-between align-items-center p-2 gap-2">
                        <Button
                          type="button"
                          icon="pi pi-arrow-left"
                          size="small"
                          text
                          onClick={() => handleMove(index, -1)}
                          disabled={disabled || uploading || index === 0}
                          aria-label="Mover a la izquierda"
                        />
                        <Button
                          type="button"
                          icon="pi pi-arrow-right"
                          size="small"
                          text
                          onClick={() => handleMove(index, 1)}
                          disabled={
                            disabled || uploading || index === imageUrls.length - 1
                          }
                          aria-label="Mover a la derecha"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="w-full text-center text-500 text-sm p-3 border-1 border-dashed surface-border border-round">
            {emptyMessage("imágenes adicionales")}
          </div>
        )}
      </div>

      {/* Cropper dialog */}
      {cropperImage && (
        <ImageCropperDialog
          visible={showCropper}
          image={cropperImage}
          cropWidth={cropWidth}
          cropHeight={cropHeight}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
};
